import { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type Tier = "advocate" | "partner" | "anchor";

const SERVICES = ["Legal", "Capital / Funding", "Build / Tech", "Distribution / Marketing", "Mentorship", "Operations", "Sourcing / Production", "Other"];

export default function PartnerApply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tierParam = (params.get("tier") as Tier) || "partner";

  const [tier, setTier] = useState<Tier>(tierParam);
  const [orgName, setOrgName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [website, setWebsite] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [counties, setCounties] = useState("");
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleService = (s: string) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const valid = useMemo(
    () => orgName.trim() && contactName.trim() && /.+@.+\..+/.test(contactEmail),
    [orgName, contactName, contactEmail],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fill in org name, contact name, and a valid email.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("partner_applications").insert({
      applicant_user_id: user?.id ?? null,
      org_name: orgName.trim(),
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      website: website.trim() || null,
      tier_requested: tier,
      services,
      counties: counties
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean),
      why: why.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not submit application", { description: error.message });
      return;
    }
    toast.success("Application received. We'll be in touch.");
    navigate("/partners");
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="px-4 sm:px-6 py-12 max-w-2xl mx-auto">
        <Link to="/partners" className="text-sm text-muted-foreground hover:text-foreground">← Back to program</Link>
        <h1 className="font-display text-3xl font-bold mt-3 mb-2">Apply to the program</h1>
        <p className="text-muted-foreground mb-8">
          Tell us about your organization. We review applications weekly and respond within five business days.
        </p>
        <Card className="p-6">
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label>Tier</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as Tier)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="advocate">Advocate — referrer / amplifier</SelectItem>
                  <SelectItem value="partner">Partner — service provider</SelectItem>
                  <SelectItem value="anchor">Anchor — strategic / funder</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org">Organization name *</Label>
                <Input id="org" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cname">Contact name *</Label>
                <Input id="cname" value={contactName} onChange={(e) => setContactName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="cemail">Contact email *</Label>
                <Input id="cemail" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="mt-1.5" />
              </div>
            </div>

            <div>
              <Label>Services you provide</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SERVICES.map((s) => {
                  const active = services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="counties">Counties you serve (comma-separated)</Label>
              <Input
                id="counties"
                value={counties}
                onChange={(e) => setCounties(e.target.value)}
                placeholder="Mobile, Baldwin, Jefferson"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="why">Why do you want to join?</Label>
              <Textarea
                id="why"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="What you bring, who you serve, anything we should know."
                className="mt-1.5 min-h-[120px]"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
              <Button type="button" variant="outline" asChild>
                <Link to="/partners">Cancel</Link>
              </Button>
            </div>
          </form>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}